import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaPencilAlt, FaChevronRight, FaBoxOpen, FaStore, FaChevronDown, FaChevronUp, FaFileExcel } from 'react-icons/fa';
import { db } from '../firebase';
import { useEffectiveUser } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import Header from '../components/Header';
import ItemCard from '../components/BusinessInventory/ItemCard';
import ItemModal from '../components/BusinessInventory/ItemModal';
import ItemSearchBar from '../components/BusinessInventory/ItemSearchBar';
import ItemDetailDrawer from '../components/BusinessInventory/ItemDetailDrawer';
import ProfileModal from '../components/BusinessInventory/ProfileModal';
import SoldItemModal from '../components/BusinessInventory/SoldItemModal';
import ConfirmationModal from '../components/ConfirmationModal';

function BusinessProfile() {
  const navigate = useNavigate();
  const { profileId } = useParams();
  const { currentUser } = useEffectiveUser();
  
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState({});
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState(['USD']); // User's selected currencies
  const [salesHistory, setSalesHistory] = useState([]); // All sales records
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ stock: '' });
  const [viewMode, setViewMode] = useState('grid');
  const [showItemModal, setShowItemModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToSold, setItemToSold] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAlertCollapsed, setIsAlertCollapsed] = useState(true); // Default collapsed

  // Fetch profile and items
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !profileId) return;

      try {
        setIsLoading(true);

        // Fetch profile
        const profilesRef = doc(db, 'businessProfiles', currentUser.uid);
        const profilesDoc = await getDoc(profilesRef);
        
        if (profilesDoc.exists()) {
          const profiles = profilesDoc.data().profiles || [];
          const currentProfile = profiles.find(p => p.id === profileId);
          
          if (currentProfile) {
            setProfile(currentProfile);
          } else {
            toast.error('Profile not found');
            navigate('/business-inventory');
            return;
          }
        } else {
          toast.error('Profile not found');
          navigate('/business-inventory');
          return;
        }

        // Fetch items
        const itemsRef = doc(db, 'inventoryItems', currentUser.uid);
        const itemsDoc = await getDoc(itemsRef);
        
        if (itemsDoc.exists()) {
          const allItems = itemsDoc.data().items || {};
          // Filter items for this profile
          const profileItems = Object.fromEntries(
            Object.entries(allItems).filter(([_, item]) => item.profileId === profileId)
          );
          setItems(profileItems);
        }

        // Fetch categories
        const categoriesRef = doc(db, 'inventoryCategories', currentUser.uid);
        const categoriesDoc = await getDoc(categoriesRef);
        
        if (categoriesDoc.exists()) {
          setCategories(categoriesDoc.data().categories || []);
        } else {
          // Initialize empty categories
          await setDoc(categoriesRef, { 
            userId: currentUser.uid,
            categories: [] 
          });
          setCategories([]);
        }

        // Fetch user's currency settings
        const settingsRef = doc(db, 'userSettings', currentUser.uid);
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists()) {
          const userSettings = settingsDoc.data();
          setCurrencies(userSettings.currencies || ['USD']);
        } else {
          setCurrencies(['USD']);
        }

        // Fetch sales history for this profile
        const salesRef = doc(db, 'inventorySales', currentUser.uid);
        const salesDoc = await getDoc(salesRef);
        
        if (salesDoc.exists()) {
          const allSales = salesDoc.data().sales || [];
          // Filter sales for this specific profile
          const profileSales = allSales.filter(sale => sale.profileId === profileId);
          setSalesHistory(profileSales);
        } else {
          setSalesHistory([]);
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
        toast.error('Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentUser, profileId, navigate]);

  const updateCategories = async (newCategories) => {
    try {
      const categoriesRef = doc(db, 'inventoryCategories', currentUser.uid);
      await updateDoc(categoriesRef, { categories: newCategories });
      setCategories(newCategories);
      toast.success('Category added successfully');
    } catch (error) {
      console.error('Error updating categories:', error);
      toast.error('Failed to update categories');
    }
  };

  const handleItemSubmit = async (itemData) => {
    try {
      const itemsRef = doc(db, 'inventoryItems', currentUser.uid);
      const itemsDoc = await getDoc(itemsRef);
      const allItems = itemsDoc.exists() ? itemsDoc.data().items || {} : {};

      if (editingItem) {
        // Update existing item
        const updatedItem = {
          ...allItems[editingItem.id],
          ...itemData,
          updatedAt: new Date()
        };
        allItems[editingItem.id] = updatedItem;
        
        await updateDoc(itemsRef, { items: allItems });
        
        // Update local state for this profile only
        setItems(prev => ({
          ...prev,
          [editingItem.id]: updatedItem
        }));
        
        toast.success('Item updated successfully');
      } else {
        // Create new item
        const itemId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newItem = {
          ...itemData,
          id: itemId
        };
        
        allItems[itemId] = newItem;
        await updateDoc(itemsRef, { items: allItems });
        
        // Add to local state
        setItems(prev => ({
          ...prev,
          [itemId]: newItem
        }));
        
        toast.success('Item added successfully');
      }
      
      setShowItemModal(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Failed to save item');
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowItemModal(true);
  };

  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      const itemsRef = doc(db, 'inventoryItems', currentUser.uid);
      const itemsDoc = await getDoc(itemsRef);
      const allItems = itemsDoc.data().items || {};
      
      delete allItems[itemToDelete.id];
      
      await updateDoc(itemsRef, { items: allItems });
      
      setItems(prev => {
        const newItems = { ...prev };
        delete newItems[itemToDelete.id];
        return newItems;
      });
      
      toast.success('Item deleted successfully');
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleDuplicateItem = async (item) => {
    try {
      // Generate duplicate name
      const baseName = item.name || 'Untitled Item';
      let duplicateName = `${baseName} - duplicate`;
      let counter = 1;

      // Check if duplicates already exist and increment counter (only check items from same profile)
      const existingItems = Object.values(items).filter(existingItem => existingItem.profileId === profileId);
      while (existingItems.some(existingItem => existingItem.name === duplicateName)) {
        counter++;
        duplicateName = `${baseName} - duplicate ${counter}`;
      }

      // Create new item with duplicate name
      const newItemId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const { id, ...itemWithoutId } = item; // Remove original id
      const duplicatedItem = {
        ...itemWithoutId,
        name: duplicateName,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Save to Firestore
      const itemsRef = doc(db, 'inventoryItems', currentUser.uid);
      const itemsDoc = await getDoc(itemsRef);
      const allItems = itemsDoc.data().items || {};
      
      allItems[newItemId] = duplicatedItem;
      
      await updateDoc(itemsRef, { items: allItems });

      // Update local state (include id in the item object)
      setItems(prev => ({
        ...prev,
        [newItemId]: { ...duplicatedItem, id: newItemId }
      }));

      toast.success(`✅ Item duplicated as "${duplicateName}"`);
    } catch (error) {
      console.error('Error duplicating item:', error);
      toast.error('Failed to duplicate item');
    }
  };

  const handleSoldItem = (item) => {
    setItemToSold(item);
    setShowSoldModal(true);
  };

  const handleSoldSubmit = async (saleData) => {
    if (!itemToSold) return;

    try {
      // Update item quantity
      const newQuantity = itemToSold.quantity - saleData.quantitySold;
      
      const itemsRef = doc(db, 'inventoryItems', currentUser.uid);
      const itemsDoc = await getDoc(itemsRef);
      const allItems = itemsDoc.data().items || {};
      
      allItems[itemToSold.id] = {
        ...allItems[itemToSold.id],
        quantity: newQuantity,
        updatedAt: new Date()
      };
      
      await updateDoc(itemsRef, { items: allItems });
      
      // Update local state
      setItems(prev => ({
        ...prev,
        [itemToSold.id]: {
          ...prev[itemToSold.id],
          quantity: newQuantity,
          updatedAt: new Date()
        }
      }));

      // Record sale in inventorySales collection
      const salesRef = doc(db, 'inventorySales', currentUser.uid);
      const salesDoc = await getDoc(salesRef);
      const existingSales = salesDoc.exists() ? salesDoc.data().sales || [] : [];
      
      const saleRecord = {
        ...saleData,
        saleId: `sale_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        profileId: profileId,
        userId: currentUser.uid
      };
      
      if (salesDoc.exists()) {
        await updateDoc(salesRef, { 
          sales: [...existingSales, saleRecord] 
        });
      } else {
        await setDoc(salesRef, { 
          userId: currentUser.uid,
          sales: [saleRecord] 
        });
      }
      
      // Update local sales history
      setSalesHistory(prev => [...prev, saleRecord]);
      
      // Show appropriate notification based on remaining stock
      if (newQuantity === 0) {
        toast.warning(`⚠️ ${itemToSold.name} is now OUT OF STOCK!`, { autoClose: 5000 });
      } else if (newQuantity < 3) {
        toast.warning(`⚠️ ${itemToSold.name} is running low (${newQuantity} left)`, { autoClose: 5000 });
      } else {
        toast.success(`✅ Sale recorded! ${saleData.quantitySold} item(s) sold`);
      }
      
      setShowSoldModal(false);
      setItemToSold(null);
    } catch (error) {
      console.error('Error recording sale:', error);
      toast.error('Failed to record sale');
    }
  };

  const handleEditProfile = () => {
    setShowProfileModal(true);
  };

  const handleProfileUpdate = async (formData) => {
    try {
      const profilesRef = doc(db, 'businessProfiles', currentUser.uid);
      const profilesDoc = await getDoc(profilesRef);
      const profiles = profilesDoc.data().profiles || [];
      
      const updatedProfiles = profiles.map(p =>
        p.id === profileId
          ? { ...p, ...formData, updatedAt: new Date() }
          : p
      );
      
      await updateDoc(profilesRef, { profiles: updatedProfiles });
      
      const updatedProfile = updatedProfiles.find(p => p.id === profileId);
      setProfile(updatedProfile);
      
      toast.success('Profile updated successfully');
      setShowProfileModal(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  // Helper function to get sales count for an item
  const getSalesCount = (itemId) => {
    return salesHistory.filter(sale => sale.itemId === itemId)
      .reduce((sum, sale) => sum + sale.quantitySold, 0);
  };

  // Export items to Excel
  const handleExportToExcel = () => {
    try {
      if (!profile || !profileId) {
        toast.error('Profile information not available');
        return;
      }

      // Convert items object to array and filter for current profile
      const itemsArray = Object.entries(items)
        .filter(([_, item]) => item.profileId === profileId)
        .map(([itemId, item]) => {
          // Calculate total sales for this item
          const totalSales = getSalesCount(itemId);
          
          // Format dates
          const formatDate = (date) => {
            if (!date) return '';
            if (date.toDate) {
              return date.toDate().toLocaleDateString();
            }
            if (date instanceof Date) {
              return date.toLocaleDateString();
            }
            return new Date(date).toLocaleDateString();
          };

          return {
            'Item Name': item.name || '',
            'Price': item.price ? `${item.price} ${item.priceCurrency || 'USD'}` : '',
            'Quantity': item.quantity !== undefined && item.quantity !== null ? item.quantity : '',
            'Category': item.category || '',
            'SKU': item.sku || '',
            'Cost Price': item.costPrice ? `${item.costPrice} ${item.costPriceCurrency || 'USD'}` : '',
            'Supplier': item.supplier || '',
            'Location': item.location || '',
            'Product URL': item.productUrl || '',
            'Image URL': item.imageUrl || '',
            'Notes': item.notes || '',
            'Total Sales': totalSales,
            'Needs Restock Alert': item.needsRestock !== false ? 'Yes' : 'No',
            'Created At': formatDate(item.createdAt),
            'Updated At': formatDate(item.updatedAt)
          };
        });

      if (itemsArray.length === 0) {
        toast.error('No items to export');
        return;
      }

      // Create a new workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(itemsArray);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Inventory Items');

      // Set column widths for better readability
      const colWidths = [
        { wch: 30 }, // Item Name
        { wch: 15 }, // Price
        { wch: 10 }, // Quantity
        { wch: 20 }, // Category
        { wch: 15 }, // SKU
        { wch: 15 }, // Cost Price
        { wch: 20 }, // Supplier
        { wch: 15 }, // Location
        { wch: 40 }, // Product URL
        { wch: 40 }, // Image URL
        { wch: 30 }, // Notes
        { wch: 12 }, // Total Sales
        { wch: 18 }, // Needs Restock Alert
        { wch: 12 }, // Created At
        { wch: 12 }  // Updated At
      ];
      ws['!cols'] = colWidths;

      // Generate Excel file and download
      // Sanitize profile name for filename (remove invalid characters)
      const sanitizedName = profile.name.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
      const fileName = `${sanitizedName}_Inventory_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success(`✅ Exported ${itemsArray.length} item(s) to Excel`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  // Get low stock items (quantity < 3 or out of stock), only items with needsRestock enabled
  const lowStockItems = Object.entries(items)
    .filter(([itemId, item]) => 
      item.profileId === profileId && 
      item.quantity < 3 && 
      item.needsRestock !== false  // Only show items that have restock enabled (default true)
    )
    .map(([itemId, item]) => ({ ...item, id: itemId }))
    .sort((a, b) => a.quantity - b.quantity); // Sort by lowest stock first


  // Filter and search items
  const filteredItems = Object.entries(items).filter(([itemId, item]) => {
    const title = item.name || '';
    const quantity = item.quantity || 0;

    // Search filter
    if (searchQuery && !title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Stock filter
    if (filters.stock) {
      if (filters.stock === 'in-stock' && quantity === 0) return false;
      if (filters.stock === 'low-stock' && (quantity === 0 || quantity >= 10)) return false;
      if (filters.stock === 'out-of-stock' && quantity > 0) return false;
    }

    return true;
  }).map(([itemId, item]) => ({ ...item, id: itemId }));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-blue-50 dark:bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800">
        <Header />
        <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-600 dark:text-gray-400">Loading...</div>
          </div>
        </main>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="min-h-screen bg-blue-50 dark:bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white select-none">
      <Header />
      
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-4 flex items-center text-sm text-gray-600 dark:text-gray-400">
          <button 
            onClick={() => navigate('/business-inventory')}
            className="hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            Business Inventory
          </button>
          <FaChevronRight className="mx-2 h-3 w-3" />
          <span className="text-gray-900 dark:text-white">{profile.name}</span>
        </div>

        {/* Profile Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
            <div className="flex items-center space-x-4">
              {/* Store Logo/Image or Icon */}
              {profile.storeImageUrl ? (
                <div className="h-16 w-16 rounded-lg overflow-hidden flex-shrink-0 bg-white dark:bg-gray-700">
                  <img 
                    src={profile.storeImageUrl} 
                    alt={profile.name}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div 
                    className="h-16 w-16 rounded-lg bg-gradient-to-r from-teal-400 to-cyan-600 flex items-center justify-center"
                    style={{ display: 'none' }}
                  >
                    <FaStore className="text-white text-3xl" />
                  </div>
                </div>
              ) : (
                <div className="h-16 w-16 rounded-lg bg-gradient-to-r from-teal-400 to-cyan-600 
                  flex items-center justify-center flex-shrink-0">
                  <FaStore className="text-white text-3xl" />
                </div>
              )}
              <div>
                <div className="flex items-center space-x-2">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {profile.name}
                  </h1>
                  <button
                    onClick={handleEditProfile}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 
                      dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
                  >
                    <FaPencilAlt className="h-5 w-5" />
                  </button>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400">{profile.description}</p>
                <div className="flex items-center mt-2 space-x-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 w-full lg:w-auto">
              {profile.storeUrl && (
                <a
                  href={profile.storeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-3 py-2 sm:px-4 bg-gradient-to-r from-blue-500 to-indigo-600 
                    text-white rounded-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap text-sm sm:text-base"
                >
                  <FaStore className="mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Open Shop</span>
                  <span className="sm:hidden">Shop</span>
                </a>
              )}
              <button
                onClick={handleExportToExcel}
                className="flex items-center px-3 py-2 sm:px-4 bg-gradient-to-r from-orange-500 to-red-600 
                  text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap text-sm sm:text-base"
                title="Export all items to Excel"
              >
                <FaFileExcel className="mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Export Excel</span>
                <span className="sm:hidden">Export</span>
              </button>
              <button
                onClick={() => {
                  setEditingItem(null);
                  setShowItemModal(true);
                }}
                className="flex items-center px-3 py-2 sm:px-4 bg-gradient-to-r from-emerald-500 to-green-600 
                  text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap text-sm sm:text-base"
              >
                <FaPlus className="mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Add Item</span>
                <span className="sm:hidden">Add Item</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="mb-6">
          <ItemSearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filters={filters}
            onFilterChange={setFilters}
            onViewModeChange={setViewMode}
            viewMode={viewMode}
          />
        </div>

        {/* Low Stock Alert Section - Collapsible */}
        {lowStockItems.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-gradient-to-r from-red-50 to-yellow-50 dark:from-red-900/20 dark:to-yellow-900/20 
              rounded-xl border-2 border-red-200 dark:border-red-800 overflow-hidden"
          >
            {/* Collapsible Header */}
            <button
              onClick={() => setIsAlertCollapsed(!isAlertCollapsed)}
              className="w-full p-4 flex items-center justify-between hover:bg-red-100/50 
                dark:hover:bg-red-900/30 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 rounded-full bg-red-500 flex items-center justify-center pb-1">
                  <span className="text-white text-lg">⚠️</span>
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-red-800 dark:text-red-300">
                    {lowStockItems.length} Item{lowStockItems.length !== 1 ? 's' : ''} Need{lowStockItems.length === 1 ? 's' : ''} Attention
                  </h3>
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {isAlertCollapsed ? 'Click to expand' : 'Click to collapse'}
                  </p>
                </div>
              </div>
              {isAlertCollapsed ? (
                <FaChevronDown className="h-5 w-5 text-red-600 dark:text-red-400" />
              ) : (
                <FaChevronUp className="h-5 w-5 text-red-600 dark:text-red-400" />
              )}
            </button>

            {/* Collapsible Content */}
            {!isAlertCollapsed && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="px-4 pb-4"
              >
                <div className="space-y-2">
                  {lowStockItems.map((item) => (
                    <div 
                      key={item.id}
                      className="flex justify-between items-center bg-white dark:bg-gray-800 rounded-lg p-3 gap-3"
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <FaBoxOpen className={`h-5 w-5 flex-shrink-0 ${
                          item.quantity === 0 ? 'text-red-600' : 'text-yellow-600'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-white truncate">
                            {item.name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {item.quantity === 0 ? 'OUT OF STOCK' : `Only ${item.quantity} left`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleEditItem(item)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm 
                          hover:bg-indigo-700 transition-colors whitespace-nowrap flex-shrink-0"
                      >
                        Restock
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Items Display */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
            <FaBoxOpen className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              {Object.keys(items).length === 0 ? 'No items yet' : 'No items match your filters'}
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {Object.keys(items).length === 0 
                ? 'Start adding items to your inventory'
                : 'Try adjusting your search or filters'}
            </p>
            {Object.keys(items).length === 0 && (
              <button
                onClick={() => setShowItemModal(true)}
                className="mt-6 inline-flex items-center px-6 py-3 bg-gradient-to-r 
                  from-emerald-500 to-green-600 text-white rounded-lg hover:shadow-lg transition-all"
              >
                <FaPlus className="mr-2" />
                Add Your First Item
              </button>
            )}
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
            : 'space-y-3'
          }>
            {filteredItems.map((item, index) => (
              <ItemCard
                key={item.id}
                item={item}
                viewMode={viewMode}
                onClick={() => setSelectedItem(item)}
                onSold={() => handleSoldItem(item)}
                onDuplicate={() => handleDuplicateItem(item)}
                onEdit={() => handleEditItem(item)}
                onDelete={() => handleDeleteItem(item)}
                salesCount={getSalesCount(item.id)}
                index={index}
              />
            ))}
          </div>
        )}
      </main>

      <ItemModal
        isOpen={showItemModal}
        onClose={() => {
          setShowItemModal(false);
          setEditingItem(null);
        }}
        onSubmit={handleItemSubmit}
        profileId={profileId}
        editingItem={editingItem}
        categories={categories}
        updateCategories={updateCategories}
        currencies={currencies}
      />

      <ItemDetailDrawer
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
        onSold={handleSoldItem}
        onUpdate={handleEditItem}
        salesHistory={salesHistory}
      />

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onSubmit={handleProfileUpdate}
        editingProfile={profile}
      />

      <SoldItemModal
        isOpen={showSoldModal}
        onClose={() => {
          setShowSoldModal(false);
          setItemToSold(null);
        }}
        onSubmit={handleSoldSubmit}
        item={itemToSold}
        currencies={currencies}
      />

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDeleteItem}
        title="Delete Item"
        message={`Are you sure you want to delete "${itemToDelete?.name || 'this item'}"? This action cannot be undone.`}
      />
    </div>
  );
}

export default BusinessProfile;

